// Mind › Vision's panel section: a local drawing, image or camera as the stimulus, on ba-mind's live
// layer (features/mindLive.ts). The camera is off until switched on here, frames stay in this browser and
// nothing is uploaded; the section says so. Numbers and the field view are live on any viewport; the cortex
// picture follows when the viewport takes live vertex layers.

import { useEffect, useRef } from 'react';
import { useAtlas } from '../app/store.ts';
import { stillView } from '../app/scene.ts';
import { openedSlot } from '../app/sims.ts';
import { getViewport } from '../viewport/host.ts';
import { Button, Checkbox, Section, Switch } from '../ui/primitives.tsx';
import { bindLive, clearPaint, finishPaintStroke, paintStroke, requestSource, setMirror, setWavePlaying, setWaveValue, startCamera, startImage, startPaint, startWave, stopLive, useMindLive } from './mindLive.ts';
import { PAINT_SPOTS } from './mindPaint.ts';
import { WAVE } from './mindWave.ts';
import './mindPaint.css';

export function MindSections() {
  const main = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main' && x.format === 'dynamics' && x.path.startsWith('mind/')) : undefined));
  const path = main?.path;
  // A still view of the scene (Words into maps) draws no cortex: the camera and image stay off.
  const still = useAtlas(stillView);
  const live = useMindLive();
  const file = useRef<HTMLInputElement>(null);

  // The run's uv and validity, and the viewport, bound for the layer; the source stops with the scene.
  useEffect(() => {
    if (!path || still) return;
    let on = true;
    void (async () => {
      const o = openedSlot('main');
      const vp = await getViewport();
      if (!on || !o || o.format !== 'dynamics' || o.path !== path) return;
      const [uv, valid] = await Promise.all([o.sim.array('uv'), o.sim.array('valid')]);
      if (!on) return;
      bindLive(vp, uv instanceof Float32Array && valid instanceof Uint8Array ? { uv, valid } : null);
      // A link's `live=paint` (app/url.ts → requestSource) starts the drawing once the lookup is bound, as the Paint button would.
      if (useMindLive.getState().wanted === 'paint') {
        useAtlas.getState().setTracesOpen(true);
        void startPaint();
      }
    })();
    return () => {
      on = false;
      bindLive(null, null);
      void stopLive();
    };
  }, [path, still]);

  if (!path || still) return null;
  const onCamera = live.kind === 'camera' && live.status !== 'error';
  const onPaint = live.kind === 'paint';
  const wave = live.kind === 'wedge' || live.kind === 'ring' ? live.kind : null;
  const local = onPaint || wave !== null;
  const status =
    live.status === 'error'
      ? `Couldn’t start: ${live.error ?? 'unknown'}`
      : live.status === 'starting'
        ? live.kind === 'camera'
          ? 'Asking for the camera…'
          : local
            ? 'Preparing the field…'
            : 'Reading the image…'
        : live.status === 'on'
          ? live.kind === 'camera'
            ? `Camera on · ${live.frames.toLocaleString()} frames`
            : wave
              ? `${wave === 'wedge' ? 'Wedge · 30° wide' : 'Ring · 1° wide'} · ${live.wavePlaying ? 'playing' : 'paused'}`
            : onPaint
              ? 'Your drawing · white brush, 1° wide'
              : `Your image: ${live.label ?? 'chosen'}`
          : 'The run’s own bar is shown.';
  return (
    <Section title="Your stimulus">
      <div className="mind-live">
        <input
          ref={file}
          type="file"
          accept="image/*"
          className="mind-file"
          aria-label="Choose an image for the visual field"
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            if (f) void startImage(f, f.name);
            e.currentTarget.value = '';
          }}
        />
        <div className="mind-live-row">
          <Button aria-pressed={onPaint} onClick={() => { useAtlas.getState().setTracesOpen(true); if (!onPaint || live.status === 'error') void startPaint(); }}>Paint</Button>
          {(['wedge', 'ring'] as const).map((kind) => (
            <Button key={kind} aria-pressed={wave === kind} onClick={() => { useAtlas.getState().setTracesOpen(true); if (wave !== kind || live.status === 'error') void startWave(kind); }}>{kind === 'wedge' ? 'Wedge' : 'Ring'}</Button>
          ))}
          <Button onClick={() => file.current?.click()}>Choose an image…</Button>
          {live.kind ? <Button onClick={() => { requestSource(null); void stopLive(); }}>Back to the bar</Button> : null}
        </div>
        {onPaint ? (
          <div className="mind-paint-tools">
            <p className="ba-type-caption muted">Drag in the visual field below, or place a spot.</p>
            <div className="mind-paint-spots" role="group" aria-label="Paint a quadrant spot">
              {PAINT_SPOTS.map((spot) => (
                <Button key={spot.label} disabled={live.status !== 'on'} onClick={() => { paintStroke(spot); finishPaintStroke(); }}>{spot.label}</Button>
              ))}
            </div>
            <Button disabled={live.status !== 'on'} onClick={clearPaint}>Clear</Button>
          </div>
        ) : null}
        {wave ? (
          <div className="mind-wave-tools">
            <div className="mind-wave-value ba-type-small">
              <span>{wave === 'wedge' ? 'Polar angle' : 'Distance from fixation'}</span>
              <output>{wave === 'wedge' ? live.waveValue.toFixed(0) : live.waveValue.toFixed(2)}°</output>
            </div>
            <input type="range" aria-label={wave === 'wedge' ? 'Wedge angle' : 'Ring eccentricity'} min={WAVE[wave].min} max={WAVE[wave].max} step={WAVE[wave].step}
              value={live.waveValue} disabled={live.wavePlaying || live.status !== 'on'} onChange={(e) => setWaveValue(e.currentTarget.valueAsNumber)} />
            <div className="mind-wave-play">
              <Button disabled={live.status !== 'on'} aria-pressed={live.wavePlaying} onClick={() => setWavePlaying(!live.wavePlaying)}>{live.wavePlaying ? 'Pause' : 'Play'}</Button>
              <span className="ba-type-caption muted">{wave === 'wedge' ? 'One turn in 12 s · 0° right, 90° up' : 'Outward in 8 s · 0.5–11.5°'}</span>
            </div>
          </div>
        ) : null}
        <div className="rows">
          <label className="row">
            <span>Camera</span>
            <Switch checked={onCamera} onChange={(on) => void (on ? startCamera() : stopLive())} label="Use the camera as the stimulus" />
          </label>
          {live.kind && !local ? (
            <label className="row">
              <span>Mirror</span>
              <Checkbox checked={live.mirror} onChange={(on) => void setMirror(on)} label="Mirror the image left to right" />
            </label>
          ) : null}
        </div>
        <p className="mind-status ba-type-small">{status}</p>
        <p className="ba-type-caption muted mind-note">
          {wave ? 'A steady white shape travels across the 24° field. Pause to move it yourself. Its pixels stay in this browser, and nothing is uploaded.' : onPaint ? 'Your drawing fills the 24° field. Its pixels stay in this browser, and nothing is uploaded.' : 'Your image fills the 24° field; each V1–V3 centre takes what it sees there. The camera turns on only with this switch, its frames stay in this browser, and nothing is uploaded.'}
          {live.pushed === false ? ' The cortex picture waits on the engine’s live vertex layer; the field view and the centres are live.' : ''}
        </p>
        {local ? (
          <details className="mind-paint-methods ba-type-caption">
            <summary>Methods</summary>
            <p>Template stimulus projection. {wave ? 'A synthetic wedge or ring is' : 'Your drawing is'} sampled at Benson 2014 predicted V1–V3 receptive-field centres on fsaverage5. Luminance drives the cortical overlay.</p>
          </details>
        ) : null}
      </div>
    </Section>
  );
}
